"use client";

import Link from "next/link";
import { useMemo, useState } from "react";

import TeamCrest from "@/components/TeamCrest";
import TeamKit from "@/components/TeamKit";
import type { DirectoryClub } from "@/lib/club-directory";

function searchable(value: string) {
  return value.normalize("NFKC").toLocaleLowerCase("ru-RU").replace(/ё/g, "е")
    .replace(/[«»“”"']/g, "").replace(/[‐‑–—-]/g, " ").replace(/\s+/g, " ").trim();
}

export default function ClubDirectory({ clubs, publicMode = false }: { clubs: DirectoryClub[]; publicMode?: boolean }) {
  const [query, setQuery] = useState("");
  const visible = useMemo(() => {
    const words = searchable(query).split(" ").filter(Boolean);
    return clubs.filter((club) => {
      const text = searchable(`${club.name} ${club.short} ${club.city}`);
      return words.every((word) => text.includes(word));
    });
  }, [clubs, query]);

  return (
    <section className="glass-card overflow-hidden rounded-2xl" aria-label="Справочник команд">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-emerald-400/15 p-5">
        <div className="w-full sm:max-w-md">
          <label htmlFor="club-search" className="mb-2 block text-xs font-bold uppercase tracking-wider text-emerald-200/60">
            Найти команду
          </label>
          <div className="relative">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-emerald-200/40" aria-hidden="true">
              <circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" />
            </svg>
            <input
              id="club-search" type="search" value={query} maxLength={200}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Название, сокращение или город…"
              autoComplete="off"
              className="w-full rounded-xl border border-emerald-400/25 bg-emerald-950/60 py-3 pl-12 pr-4 text-sm text-white outline-none placeholder:text-emerald-200/35 focus:border-emerald-300"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          {query ? <button type="button" onClick={() => setQuery("")} className="text-xs font-semibold text-emerald-300 hover:underline">Очистить поиск</button> : null}
          <p role="status" aria-live="polite" className="text-xs font-semibold text-emerald-200/60">
            Показано {visible.length} из {clubs.length}
          </p>
        </div>
      </div>

      {visible.length ? (
        <ul className="grid grid-cols-1 gap-px bg-emerald-400/10 sm:grid-cols-2" aria-label="Все команды" data-testid="club-directory">
          {visible.map((club) => (
            <li key={club.id} data-club-id={club.id} className="min-w-0 bg-[#081d15]">
              <Link href={publicMode ? `/clubs/${club.id}` : `/admin/clubs/${club.id}`} className="group flex min-h-24 items-center gap-3 px-4 py-4 transition hover:bg-emerald-400/10 focus-visible:outline-2 focus-visible:outline-emerald-300 sm:gap-4 sm:px-5">
                <TeamCrest name={club.name} short={club.short} colors={club.colors} logo={club.logo} pattern={club.kitPattern} size={38} />
                <div className="min-w-0 flex-1">
                  <h2 className="break-words text-base font-bold leading-snug text-white group-hover:text-emerald-100">{club.name}</h2>
                  {club.city ? <p className="mt-0.5 text-xs text-emerald-200/55">{club.city}</p> : null}
                  <span className="mt-2 flex items-center gap-1" aria-label="Цвета команды">
                    {club.colors.map((color, index) => <span key={index} className="h-2 w-5 rounded-sm ring-1 ring-white/20" style={{ background: color }} />)}
                    {club.tournamentCount === 0 ? <span className="ml-2 text-[11px] text-emerald-200/40">Без участия в турнирах</span> : null}
                  </span>
                </div>
                <TeamKit name={club.name} colors={club.colors} pattern={club.kitPattern} size={32} className="hidden shrink-0 sm:block" />
                <span className="shrink-0 text-lg text-emerald-300 transition group-hover:translate-x-1" aria-hidden="true">→</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="px-5 py-12 text-center">
          <h2 className="font-bold text-white">{clubs.length ? "Команды не найдены" : "Команд пока нет"}</h2>
          <p className="mt-2 text-sm text-emerald-200/55">
            {clubs.length ? "Попробуйте другое название или город." : "Команды появятся здесь после добавления турнира."}
          </p>
          {query ? <button type="button" onClick={() => setQuery("")} className="mt-4 rounded-full border border-emerald-300/35 px-5 py-2 text-sm font-bold text-emerald-200 hover:bg-emerald-400/10">Показать все команды</button>
            : <Link href="/admin/import" className="mt-4 inline-block text-sm font-bold text-emerald-300 hover:underline">Импортировать турнир →</Link>}
        </div>
      )}
    </section>
  );
}
